import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaDownload, FaPrint } from "react-icons/fa";
import { hotelAssets } from "../assets/hotelAssets";
import {
  HOTEL_ADDRESS,
  HOTEL_INVOICE_PHONE,
  HOTEL_LEGAL_NAME,
  HOTEL_NAME,
  HOTEL_PAYMENT_NAME,
} from "../config/hotel";
import { formatDisplayDate } from "../utils/dateUtils";

const money = (value) =>
  `Rs. ${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const parseAmount = (value) => parseFloat(String(value || 0).replace(/[^\d.]/g, "")) || 0;

const getStoredBooking = () => {
  try {
    const rawBooking =
      sessionStorage.getItem("lastInvoiceBooking") || localStorage.getItem("lastInvoiceBooking");
    return rawBooking ? JSON.parse(rawBooking) : null;
  } catch {
    return null;
  }
};

const getInvoiceHtml = (invoiceMarkup, title) => `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${title}</title>
    <style>
      @page { size: A4 portrait; margin: 10mm; }
      body { margin: 0; background: #f1f5f9; color: #0f172a; font-family: Arial, sans-serif; }
      .invoice-shell { padding: 32px; }
      .invoice-paper { max-width: 820px; margin: 0 auto; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 40px; }
      @media print {
        html, body { width: 210mm; min-height: 0; background: #fff; font-size: 11px; }
        .invoice-shell { padding: 0; }
        .invoice-paper { max-width: none; border: 0; border-radius: 0; box-shadow: none; padding: 0; }
        .invoice-paper * { break-inside: avoid; }
      }
    </style>
  </head>
  <body>
    <div class="invoice-shell">${invoiceMarkup}</div>
  </body>
</html>`;

const Invoice = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const booking = location.state?.booking || getStoredBooking();

  const searchParams = new URLSearchParams(location.search);
  const flowParam = searchParams.get("flow");
  const isAdmin = !!localStorage.getItem("park_admin_token");
  
  const isCustomerFlow = flowParam === "customer"
    ? true
    : flowParam === "admin"
      ? false
      : (location.state?.fromCustomerFlow !== undefined
          ? location.state.fromCustomerFlow
          : !isAdmin);

  const backPath = isCustomerFlow ? "/" : "/adminhome";

  const totals = useMemo(() => {
    const amount = parseAmount(booking?.amount);
    const gst = (amount * 0.12) / 1.12;
    return {
      amount,
      gst,
      tariffWithoutGst: amount - gst,
    };
  }, [booking?.amount]);

  if (!booking) {
    return (
      <div className="min-h-screen bg-slate-100 p-6 text-slate-900">
        <div className="mx-auto max-w-xl rounded-md border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-bold">Invoice not available</h1>
          <p className="mt-2 text-sm text-slate-600">
            Please open an invoice from the bookings page.
          </p>
          <button
            type="button"
            onClick={() => navigate(backPath)}
            className="mt-5 inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
          >
            <FaArrowLeft /> {isCustomerFlow ? "Back to Home" : "Back to bookings"}
          </button>
        </div>
      </div>
    );
  }

  const invoiceTitle = `Invoice - ${booking.bookingId || booking.id || "booking"}`;

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const invoiceElement = document.getElementById("invoice-print-area");
    if (!invoiceElement) return;

    const html = getInvoiceHtml(invoiceElement.outerHTML, invoiceTitle);
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${invoiceTitle.replace(/[^a-z0-9-]+/gi, "-").toLowerCase()}.html`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-5 text-slate-900 md:px-6">
      <style>{`
        @page {
          size: A4 portrait;
          margin: 10mm;
        }

        @media print {
          .invoice-toolbar { display: none !important; }
          html,
          body {
            width: 210mm !important;
            min-height: 0 !important;
            overflow: visible !important;
            background: #fff !important;
            font-size: 11px !important;
          }

          #invoice-print-area {
            width: 100% !important;
            max-width: none !important;
            min-height: 0 !important;
            margin: 0 !important;
            border: 0 !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            padding: 0 !important;
          }

          #invoice-print-area * {
            break-inside: avoid;
          }

          .invoice-logo { height: 42px !important; width: 42px !important; }
          .invoice-header { padding-bottom: 10px !important; }
          .invoice-title { margin-top: 6px !important; font-size: 18px !important; }
          .invoice-meta { margin-top: 12px !important; gap: 8px !important; font-size: 11px !important; }
          .invoice-section { margin-top: 12px !important; padding-top: 12px !important; gap: 16px !important; font-size: 11px !important; }
          .invoice-section h3 { margin-bottom: 4px !important; font-size: 13px !important; }
          .invoice-lines { margin-top: 16px !important; padding-top: 10px !important; font-size: 11px !important; }
          .invoice-row { padding-top: 5px !important; padding-bottom: 5px !important; }
          .invoice-total { margin-top: 10px !important; padding-top: 10px !important; }
          .invoice-total p { font-size: 16px !important; }
          .invoice-footer { margin-top: 14px !important; font-size: 10px !important; }
        }
      `}</style>

      <div className="invoice-toolbar mx-auto mb-5 flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-yellow-600">
            Front Desk Invoice
          </p>
          <h1 className="text-2xl font-bold">{booking.bookingId || "Invoice"}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate(backPath)}
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-50"
          >
            <FaArrowLeft /> {isCustomerFlow ? "Back to Home" : "Back to Bookings"}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            <FaDownload /> Download
          </button>
          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
          >
            <FaPrint /> Print
          </button>
        </div>
      </div>

      <main
        id="invoice-print-area"
        className="invoice-paper mx-auto max-w-4xl rounded-md border border-slate-200 bg-white p-8 shadow-sm md:p-10"
      >
        <header className="invoice-header border-b border-slate-200 pb-6 text-center">
          <img src={hotelAssets.logo} alt={HOTEL_NAME} className="invoice-logo mx-auto h-16 w-16 object-contain" />
          <h2 className="invoice-title mt-3 text-2xl font-bold">{HOTEL_LEGAL_NAME}</h2>
          <p className="text-sm font-semibold text-slate-600">{HOTEL_NAME}</p>
          <p className="mt-2 text-xs text-slate-500">Address: {HOTEL_ADDRESS}</p>
          <p className="text-xs text-slate-500">Phone: {HOTEL_INVOICE_PHONE}</p>
        </header>

        <section className="invoice-meta mt-6 grid gap-4 text-sm md:grid-cols-2">
          <div>
            <p>
              <span className="font-semibold">Invoice No:</span> {booking.bookingId || "N/A"}
            </p>
            <p>
              <span className="font-semibold">Invoice Date:</span>{" "}
              {new Date().toLocaleDateString("en-IN")}
            </p>
          </div>
          <div className="md:text-right">
            <p>
              <span className="font-semibold">Room No:</span> {booking.roomNumber || "N/A"}
            </p>
            <p>
              <span className="font-semibold">Booking Date:</span>{" "}
              {formatDisplayDate(booking.createdAt || booking.checkInDate)}
            </p>
          </div>
        </section>

        <section className="invoice-section mt-6 grid gap-6 border-t border-slate-200 pt-6 text-sm md:grid-cols-2">
          <div>
            <h3 className="mb-2 text-base font-bold">Guest Details</h3>
            <p>
              <span className="font-semibold">Guest Name:</span> {booking.customerName || "N/A"}
            </p>
            <p>
              <span className="font-semibold">Contact:</span> {booking.phoneNumber || "N/A"}
            </p>
            {booking.email && (
              <p>
                <span className="font-semibold">Email:</span> {booking.email}
              </p>
            )}
            <p>
              <span className="font-semibold">Address:</span> {booking.address || "N/A"}
            </p>
          </div>

          <div>
            <h3 className="mb-2 text-base font-bold">Stay Details</h3>
            <p>
              <span className="font-semibold">Check-In:</span>{" "}
              {formatDisplayDate(booking.checkInDate)}
            </p>
            <p>
              <span className="font-semibold">Check-Out:</span>{" "}
              {formatDisplayDate(booking.checkOutDate)}
            </p>
            <p>
              <span className="font-semibold">Room Type:</span> {booking.roomType || "N/A"}
            </p>
            <p>
              <span className="font-semibold">Meal Plan:</span> {booking.mealPlan || "N/A"}
            </p>
          </div>
        </section>

        <section className="invoice-lines mt-8 border-t border-slate-200 pt-5 text-sm">
          <InvoiceRow label="Room Tariff (Including GST)" value={money(totals.amount)} />
          <InvoiceRow label="Goods and Services Tax (12%)" value={money(totals.gst)} />
          <InvoiceRow label="Tariff Without GST" value={money(totals.tariffWithoutGst)} />
        </section>

        <section className="invoice-total mt-5 border-t border-slate-200 pt-5 text-right">
          <p className="text-xl font-bold">Total: {money(totals.amount)}</p>
        </section>

        <footer className="invoice-footer mt-8 text-center text-xs text-slate-500">
          <p>
            All payments are to be made by CASH/CARD/NEFT/UPI in the name of "{HOTEL_PAYMENT_NAME}".
          </p>
          <p>If you have any queries regarding this invoice, please contact us.</p>
          <p className="mt-2 italic">We Will Do Our Best, Thank You For Your Business</p>
        </footer>
      </main>
    </div>
  );
};

const InvoiceRow = ({ label, value }) => (
  <div className="invoice-row flex justify-between gap-4 border-b border-slate-100 py-2">
    <span>{label}</span>
    <span className="font-semibold">{value}</span>
  </div>
);

export default Invoice;
